import { useCallback, useEffect, useRef, useState } from "react";
import {
  api,
  type Campaign,
  type CampaignInvitation,
  type Me,
  type PublicCampaign,
} from "./api";
import { CampaignSetupPage } from "./CampaignSetupPage";
import { GnCampaignShell } from "./gn/GnCampaignShell";
import { GnHome } from "./gn/GnHome";
import { gnUiEnabled } from "./gn/gnUi";
import { applyTheme, saveThemePreference } from "./theme";

const CAMPAIGN_LIST_REFRESH_MS = 10_000;
const GAME_UI_ASSET_BASE = "/assets/game-ui";
const CAMPAIGN_COVER_PLACEHOLDER = `${GAME_UI_ASSET_BASE}/campaign_cover_placeholder.webp`;

type HubTab = "active" | "preparing" | "archive" | "discover" | "new" | "join";

function App() {
  const [me, setMe] = useState<Me | null>(null);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [publicCampaigns, setPublicCampaigns] = useState<PublicCampaign[]>([]);
  const [invitations, setInvitations] = useState<CampaignInvitation[]>([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(
    () => new URLSearchParams(window.location.search).get("campaignId"),
  );
  const [hubTab, setHubTab] = useState<HubTab>("active");
  const [title, setTitle] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [publicVisible, setPublicVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [listRefreshing, setListRefreshing] = useState(false);
  const [syncProblem, setSyncProblem] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const listRequestRef = useRef<Promise<void> | null>(null);
  const mountedRef = useRef(true);

  const refreshCampaigns = useCallback(async (silent = true) => {
    if (listRequestRef.current) return listRequestRef.current;
    let indicatorTimer: number | undefined;
    const request = (async () => {
      if (silent)
        indicatorTimer = window.setTimeout(() => setListRefreshing(true), 450);
      try {
        const [campaignList, publicList, invitationList] = await Promise.all([
          api.campaigns(),
          api.publicCampaigns(),
          api.incomingInvitations(),
        ]);
        if (!mountedRef.current) return;
        setCampaigns(campaignList);
        setPublicCampaigns(publicList);
        setInvitations(invitationList);
        setSyncProblem(false);
      } catch (cause) {
        if (!mountedRef.current) return;
        if (silent) setSyncProblem(true);
        else
          setError(
            cause instanceof Error
              ? cause.message
              : "Не удалось загрузить кампании",
          );
      } finally {
        if (indicatorTimer) window.clearTimeout(indicatorTimer);
        if (mountedRef.current) setListRefreshing(false);
      }
    })();
    listRequestRef.current = request;
    try {
      await request;
    } finally {
      if (listRequestRef.current === request) listRequestRef.current = null;
    }
  }, []);

  const load = useCallback(async () => {
    setError(null);
    try {
      const profile = await api.me();
      const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (localTimeZone && profile.timeZoneId !== localTimeZone) {
        void api.updateTimeZone(localTimeZone).catch(() => undefined);
      }
      if (!mountedRef.current) return;
      setMe(profile);
      saveThemePreference("dark", profile.id);
      await refreshCampaigns(false);
    } catch (cause) {
      if (mountedRef.current)
        setError(
          cause instanceof Error
            ? cause.message
            : "Не удалось загрузить приложение",
        );
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [refreshCampaigns]);

  useEffect(() => {
    mountedRef.current = true;
    void load();
    return () => {
      mountedRef.current = false;
    };
  }, [load]);

  useEffect(() => {
    applyTheme("dark");
  }, []);

  useEffect(() => {
    if (selectedCampaignId || loading) return;
    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") void refreshCampaigns(true);
    };
    const timer = window.setInterval(
      refreshWhenVisible,
      CAMPAIGN_LIST_REFRESH_MS,
    );
    document.addEventListener("visibilitychange", refreshWhenVisible);
    window.addEventListener("focus", refreshWhenVisible);
    window.addEventListener("online", refreshWhenVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
      window.removeEventListener("focus", refreshWhenVisible);
      window.removeEventListener("online", refreshWhenVisible);
    };
  }, [loading, refreshCampaigns, selectedCampaignId]);

  async function createCampaign(
    titleArg: string,
    publicVisibleArg: boolean,
  ): Promise<boolean> {
    const trimmed = titleArg.trim();
    if (!trimmed) return false;
    setBusy(true);
    setError(null);
    try {
      const campaign = await api.createCampaign(trimmed, publicVisibleArg);
      setCampaigns((current) => [
        campaign,
        ...current.filter((item) => item.id !== campaign.id),
      ]);
      setTitle("");
      setPublicVisible(false);
      setSelectedCampaignId(campaign.id);
      return true;
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Не удалось создать кампанию",
      );
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function joinByCode(codeArg: string): Promise<boolean> {
    const trimmed = codeArg.trim();
    if (!trimmed) return false;
    setBusy(true);
    setError(null);
    try {
      const campaign = await api.joinCampaign(trimmed);
      setCampaigns((current) => [
        campaign,
        ...current.filter((item) => item.id !== campaign.id),
      ]);
      setInviteCode("");
      setSelectedCampaignId(campaign.id);
      return true;
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Не удалось присоединиться",
      );
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function joinPublicCampaign(campaignId: string): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      const campaign = await api.joinPublicCampaign(campaignId);
      setCampaigns((current) => [
        campaign,
        ...current.filter((item) => item.id !== campaign.id),
      ]);
      setPublicCampaigns((current) =>
        current.filter((item) => item.id !== campaign.id),
      );
      setSelectedCampaignId(campaign.id);
      return true;
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Не удалось присоединиться к публичной кампании",
      );
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function acceptInvitation(invitationId: string): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      const campaign = await api.acceptCampaignInvitation(invitationId);
      setCampaigns((current) => [
        campaign,
        ...current.filter((item) => item.id !== campaign.id),
      ]);
      setInvitations((current) =>
        current.filter((item) => item.id !== invitationId),
      );
      setSelectedCampaignId(campaign.id);
      return true;
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Не удалось принять приглашение",
      );
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function declineInvitation(invitationId: string): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      await api.declineCampaignInvitation(invitationId);
      setInvitations((current) =>
        current.filter((item) => item.id !== invitationId),
      );
      return true;
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Не удалось отклонить приглашение",
      );
      return false;
    } finally {
      setBusy(false);
    }
  }

  if (selectedCampaignId) {
    const exitCampaign = () => {
      setSelectedCampaignId(null);
      window.history.replaceState({}, "", window.location.pathname);
      void refreshCampaigns(true);
    };
    if (gnUiEnabled()) {
      return <GnCampaignShell campaignId={selectedCampaignId} onBack={exitCampaign} />;
    }
    return <CampaignSetupPage campaignId={selectedCampaignId} onBack={exitCampaign} />;
  }

  if (loading)
    return (
      <main className="shell">
        <div className="loading">Город просыпается…</div>
      </main>
    );

  if (gnUiEnabled()) {
    return (
      <GnHome
        me={me}
        campaigns={campaigns}
        publicCampaigns={publicCampaigns}
        invitations={invitations}
        busy={busy}
        listRefreshing={listRefreshing}
        syncProblem={syncProblem}
        error={error}
        onOpen={setSelectedCampaignId}
        onCreate={createCampaign}
        onJoinCode={joinByCode}
        onJoinPublic={joinPublicCampaign}
        onAccept={acceptInvitation}
        onDecline={declineInvitation}
        onReload={() => void load()}
      />
    );
  }

  const groups = groupCampaigns(campaigns);
  const visibleCampaigns =
    hubTab === "active"
      ? groups.active
      : hubTab === "preparing"
        ? groups.preparing
        : groups.archive;
  const unreadCampaigns = campaigns.filter((item) => item.hasUnread).length;
  const unreadEvents = campaigns.reduce(
    (sum, item) => sum + item.unreadCount,
    0,
  );
  return (
    <main className="shell cc-hub-shell">
      <HomeAppBar me={me} />

      {error && (
        <section className="error-card">
          {error}
          <button onClick={() => void load()}>Повторить</button>
        </section>
      )}

      <FeaturedCampaign campaigns={campaigns} onOpen={setSelectedCampaignId} />

      <section className="cc-campaign-hub cc-home-board">
        {hubTab === "discover" ? (
          <>
            <HomeModeHeader
              title="Поиск и приглашения"
              subtitle="Публичные кампании и входящие приглашения"
              onBack={() => setHubTab("active")}
            />
            <CampaignDiscovery
              invitations={invitations}
              publicCampaigns={publicCampaigns}
              busy={busy}
              onAccept={acceptInvitation}
              onDecline={declineInvitation}
              onJoinPublic={joinPublicCampaign}
            />
          </>
        ) : hubTab === "new" ? (
          <>
            <HomeModeHeader
              title="Создать кампанию"
              subtitle="Новая хроника для вашей группы"
              onBack={() => setHubTab("active")}
            />
            <section className="actions-grid cc-hub-actions cc-home-form-grid">
              <form
                className="panel cc-home-mode-card"
                onSubmit={(event) => {
                  event.preventDefault();
                  void createCampaign(title, publicVisible);
                }}
              >
                <img
                  className="cc-panel-asset-icon"
                  src={`${GAME_UI_ASSET_BASE}/icon_create_campaign.png`}
                  alt=""
                />
                <h2>Новая хроника</h2>
                <p>Начните кампанию и пригласите ещё 2–9 участников.</p>
                <label>
                  Название кампании
                  <input
                    value={title}
                    onChange={(event) => setTitle(event.target.value)}
                    maxLength={120}
                    placeholder="Например, Совет Нерея"
                  />
                </label>
                <label className="cc-check-toggle cc-visibility-toggle">
                  <input
                    type="checkbox"
                    checked={publicVisible}
                    onChange={(event) => setPublicVisible(event.target.checked)}
                  />
                  <span>
                    <strong>Публичная кампания</strong>
                    <small>Будет видна всем игрокам во вкладке поиска.</small>
                  </span>
                </label>
                <button className="primary" disabled={busy || !title.trim()}>
                  Создать кампанию
                </button>
              </form>
            </section>
          </>
        ) : hubTab === "join" ? (
          <>
            <HomeModeHeader
              title="Войти по коду"
              subtitle="Используйте код от организатора"
              onBack={() => setHubTab("active")}
            />
            <section className="actions-grid cc-hub-actions cc-home-form-grid">
              <form
                className="panel cc-home-mode-card"
                onSubmit={(event) => {
                  event.preventDefault();
                  void joinByCode(inviteCode);
                }}
              >
                <img
                  className="cc-panel-asset-icon"
                  src={`${GAME_UI_ASSET_BASE}/icon_join_code.png`}
                  alt=""
                />
                <h2>Код приглашения</h2>
                <p>
                  Введите код кампании. Если игра уже идёт, вступление будет
                  записано в хронику.
                </p>
                <label>
                  Код приглашения
                  <input
                    className="code-input"
                    value={inviteCode}
                    onChange={(event) =>
                      setInviteCode(event.target.value.toUpperCase())
                    }
                    maxLength={16}
                    placeholder="ABCD2345"
                  />
                </label>
                <button className="primary" disabled={busy || !inviteCode.trim()}>
                  Присоединиться
                </button>
              </form>
            </section>
          </>
        ) : (
          <>
            <div className="cc-home-section-heading">
              <div>
                <div className="eyebrow">Мои кампании</div>
              </div>
              <ListConnectionStatus
                listRefreshing={listRefreshing}
                syncProblem={syncProblem}
              />
            </div>
            <CampaignList
              campaigns={campaigns.slice(0, 4)}
              tab="active"
              onOpen={setSelectedCampaignId}
            />
            <HomeQuickActions
              invitationsCount={invitations.length}
              publicCampaignCount={publicCampaigns.length}
              onOpenInvitations={() => setHubTab("discover")}
              onOpenSearch={() => setHubTab("discover")}
              onOpenJoinCode={() => setHubTab("join")}
              onOpenCreate={() => setHubTab("new")}
            />
            <HubCompactSummary
              active={groups.active.length}
              preparing={groups.preparing.length}
              archive={groups.archive.length}
              unreadEvents={unreadEvents}
              unreadCampaigns={unreadCampaigns}
            />
          </>
        )}
      </section>
    </main>
  );
}


function HomeAppBar({ me }: { me: Me | null }) {
  return (
    <header className="cc-home-appbar" aria-label="Главная City Chronicle">
      <div className="cc-home-brand">
        <span>City Chronicle</span>
        {me && <small>{me.displayName}</small>}
      </div>
    </header>
  );
}

function ListConnectionStatus({
  listRefreshing,
  syncProblem,
}: {
  listRefreshing: boolean;
  syncProblem: boolean;
}) {
  return (
    <div className="cc-list-status" aria-live="polite">
      {listRefreshing ? (
        <>
          <span className="cc-spinner small" />
          Синхронизация
        </>
      ) : syncProblem ? (
        <>
          <span className="cc-live-dot warning" />
          Повторяем связь
        </>
      ) : (
        <>
          <span className="cc-live-dot" />
          Онлайн
        </>
      )}
    </div>
  );
}

function HomeModeHeader({
  title,
  subtitle,
  onBack,
}: {
  title: string;
  subtitle: string;
  onBack: () => void;
}) {
  return (
    <header className="cc-home-mode-header">
      <button type="button" onClick={onBack} aria-label="Вернуться к списку кампаний">
        ←
      </button>
      <div>
        <div className="eyebrow">Полка хроник</div>
        <h2>{title}</h2>
        <p>{subtitle}</p>
      </div>
    </header>
  );
}

function HomeQuickActions({
  invitationsCount,
  publicCampaignCount,
  onOpenInvitations,
  onOpenSearch,
  onOpenJoinCode,
  onOpenCreate,
}: {
  invitationsCount: number;
  publicCampaignCount: number;
  onOpenInvitations: () => void;
  onOpenSearch: () => void;
  onOpenJoinCode: () => void;
  onOpenCreate: () => void;
}) {
  return (
    <section className="cc-home-actions-grid" aria-label="Быстрые действия">
      <HomeActionTile
        icon="icon_invitation.png"
        title="Приглашения"
        subtitle={invitationsCount ? `${invitationsCount} новое` : "нет новых"}
        badge={invitationsCount || undefined}
        onClick={onOpenInvitations}
      />
      <HomeActionTile
        icon="icon_search_campaign.png"
        title="Поиск кампаний"
        subtitle={publicCampaignCount ? `${publicCampaignCount} публичных` : "публичные игры"}
        badge={publicCampaignCount || undefined}
        onClick={onOpenSearch}
      />
      <HomeActionTile
        icon="icon_join_code.png"
        title="Войти по коду"
        subtitle="у меня есть код"
        onClick={onOpenJoinCode}
      />
      <HomeActionTile
        icon="icon_create_campaign.png"
        title="Создать кампанию"
        subtitle="новая хроника"
        onClick={onOpenCreate}
      />
    </section>
  );
}

function HomeActionTile({
  icon,
  title,
  subtitle,
  badge,
  onClick,
}: {
  icon: string;
  title: string;
  subtitle: string;
  badge?: number;
  onClick: () => void;
}) {
  return (
    <button type="button" className="cc-home-action-tile" onClick={onClick}>
      <img src={`${GAME_UI_ASSET_BASE}/${icon}`} alt="" />
      <span>
        <strong>{title}</strong>
        <small>{subtitle}</small>
      </span>
      {badge ? <b>{badge}</b> : null}
    </button>
  );
}

function FeaturedCampaign({
  campaigns,
  onOpen,
}: {
  campaigns: Campaign[];
  onOpen: (id: string) => void;
}) {
  const featured = [...campaigns].sort((left, right) => {
    if (left.hasUnread !== right.hasUnread) return left.hasUnread ? -1 : 1;
    if (left.status !== right.status)
      return left.status === "ACTIVE" ? -1 : right.status === "ACTIVE" ? 1 : 0;
    return (
      new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()
    );
  })[0];
  if (!featured)
    return (
      <section className="cc-shelf-hero empty">
        <div className="eyebrow">Продолжить игру</div>
        <h2>Кампания ещё не выбрана</h2>
        <p>
          Создайте новую хронику, войдите по коду или присоединитесь к публичной
          кампании.
        </p>
      </section>
    );
  return (
    <section
      className={`cc-shelf-hero${featured.hasUnread ? " has-unread" : ""}`}
    >
      <CampaignCover campaign={featured} />
      <div className="cc-shelf-content">
        <div className="eyebrow">Продолжить игру</div>
        <h2>{featured.title}</h2>
        <p>{statusLabel(featured.status)}</p>
        <div className="cc-shelf-facts">
          <span>{featured.memberCount}/10 игроков</span>
          {featured.hasUnread && (
            <span>{featured.unreadCount} новых событий</span>
          )}
          {featured.memberRole === "OWNER" && (
            <span>{featured.publicVisible ? "Публичная" : "Приватная"}</span>
          )}
        </div>
        <button
          className="primary"
          type="button"
          onClick={() => onOpen(featured.id)}
        >
          Открыть кампанию
        </button>
      </div>
    </section>
  );
}

function CampaignCover({ campaign }: { campaign: Campaign }) {
  const [src, setSrc] = useState(CAMPAIGN_COVER_PLACEHOLDER);
  const coverId =
    campaign.coverImage?.status === "READY"
      ? campaign.coverImage.id
      : undefined;

  useEffect(() => {
    let disposed = false;
    let objectUrl: string | null = null;
    setSrc(CAMPAIGN_COVER_PLACEHOLDER);
    if (!coverId)
      return () => {
        disposed = true;
      };

    void api
      .cycleImageContent(campaign.id, coverId)
      .then((blob) => {
        if (disposed) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch(() => {
        if (!disposed) setSrc(CAMPAIGN_COVER_PLACEHOLDER);
      });

    return () => {
      disposed = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [campaign.id, coverId]);

  return (
    <figure
      className={`cc-shelf-cover${coverId ? " prologue-ready" : " placeholder"}`}
      style={{ backgroundImage: `url(${src})`, margin: 'auto', position: 'absolute' }}
    >
      <img
        src={src}
        alt={
          coverId
            ? `Иллюстрация пролога кампании ${campaign.title}`
            : "Обложка кампании до генерации пролога"
        }
      />
    </figure>
  );
}

function CampaignDiscovery({
  invitations,
  publicCampaigns,
  busy,
  onAccept,
  onDecline,
  onJoinPublic,
}: {
  invitations: CampaignInvitation[];
  publicCampaigns: PublicCampaign[];
  busy: boolean;
  onAccept: (id: string) => Promise<boolean>;
  onDecline: (id: string) => Promise<boolean>;
  onJoinPublic: (id: string) => Promise<boolean>;
}) {
  return (
    <section
      className="cc-discovery-grid"
      aria-label="Поиск кампаний и приглашения"
    >
      <div className="panel cc-discovery-panel">
        <img
          className="cc-panel-asset-icon"
          src={`${GAME_UI_ASSET_BASE}/icon_invitation.png`}
          alt=""
        />
        <h2>Приглашения</h2>
        <p>
          Организаторы добавили вас в кампании. Примите приглашение, чтобы войти
          в игру, или отклоните его.
        </p>
        {invitations.length === 0 ? (
          <p className="cc-muted">Активных приглашений нет.</p>
        ) : (
          <div className="cc-discovery-list">
            {invitations.map((invitation) => (
              <article className="cc-discovery-card" key={invitation.id}>
                <div>
                  <strong>{invitation.campaignTitle}</strong>
                  <span>От: {invitation.invitedByDisplayName}</span>
                </div>
                <div className="cc-inline-actions">
                  <button
                    className="primary"
                    disabled={busy}
                    type="button"
                    onClick={() => void onAccept(invitation.id)}
                  >
                    Принять
                  </button>
                  <button
                    disabled={busy}
                    type="button"
                    onClick={() => void onDecline(invitation.id)}
                  >
                    Отклонить
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
      <div className="panel cc-discovery-panel">
        <img
          className="cc-panel-asset-icon"
          src={`${GAME_UI_ASSET_BASE}/icon_search_campaign.png`}
          alt=""
        />
        <h2>Поиск кампании</h2>
        <p>
          Публичные кампании, к которым можно присоединиться без кода
          приглашения.
        </p>
        {publicCampaigns.length === 0 ? (
          <p className="cc-muted">
            Пока нет публичных кампаний со свободными местами.
          </p>
        ) : (
          <div className="cc-discovery-list">
            {publicCampaigns.map((campaign) => (
              <article className="cc-discovery-card" key={campaign.id}>
                <div>
                  <strong>{campaign.title}</strong>
                  <span>
                    {campaign.memberCount}/{campaign.maxPlayers} игроков ·
                    организатор {campaign.ownerDisplayName}
                  </span>
                </div>
                <button
                  className="primary"
                  disabled={busy || campaign.slotsLeft <= 0}
                  type="button"
                  onClick={() => void onJoinPublic(campaign.id)}
                >
                  Присоединиться
                </button>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function HubTabButton({
  active,
  label,
  count,
  onClick,
}: {
  active: boolean;
  label: string;
  count: number;
  onClick: () => void;
}) {
  return (
    <button type="button" className={active ? "active" : ""} onClick={onClick}>
      <span>{label}</span>
      <b>{count}</b>
    </button>
  );
}

function HubCompactSummary({
  active,
  preparing,
  archive,
  unreadEvents,
  unreadCampaigns,
}: {
  active: number;
  preparing: number;
  archive: number;
  unreadEvents: number;
  unreadCampaigns: number;
}) {
  return (
    <aside className="cc-hub-compact-summary" aria-label="Сводка кампаний">
      <span>Сводка</span>
      <b>{active} активных</b>
      <b>{preparing} в подготовке</b>
      {archive > 0 && <b>{archive} в архиве</b>}
      <b className={unreadEvents > 0 ? "attention" : undefined}>
        {unreadEvents > 0
          ? `${unreadEvents} новых в ${unreadCampaigns} камп.`
          : "без новых событий"}
      </b>
    </aside>
  );
}

function CampaignList({
  campaigns,
  tab,
  onOpen,
}: {
  campaigns: Campaign[];
  tab: "active" | "preparing" | "archive";
  onOpen: (id: string) => void;
}) {
  if (campaigns.length === 0)
    return (
      <div className="empty-state cc-hub-empty">
        <div className="empty-symbol">⌁</div>
        <h3>
          {tab === "active"
            ? "Нет активных кампаний"
            : tab === "preparing"
              ? "Нет кампаний в подготовке"
              : "Архив пока пуст"}
        </h3>
        <p>
          {tab === "active"
            ? "Откройте подготовку или начните новую хронику."
            : "Кампании появятся здесь автоматически."}
        </p>
      </div>
    );

  return (
    <div className="campaign-list cc-hub-campaign-list">
      {campaigns.map((campaign) => (
        <article
          className={`campaign-card${campaign.hasUnread ? " has-unread" : ""}`}
          key={campaign.id}
        >
          <button
            className="campaign-open"
            type="button"
            onClick={() => onOpen(campaign.id)}
          >
            <div className="campaign-meta">
              <span>
                {campaign.memberRole === "OWNER" ? "Организатор" : "Участник"}
              </span>
              <span>{campaign.memberCount}/10 игроков</span>
              {campaign.memberRole === "OWNER" && (
                <span>
                  {campaign.publicVisible ? "Публичная" : "Приватная"}
                </span>
              )}
              {campaign.hasUnread && (
                <span className="cc-unread-label">
                  <i />
                  Новые события {campaign.unreadCount}
                </span>
              )}
            </div>
            <h3>{campaign.title}</h3>
            <p>{statusLabel(campaign.status)}</p>
          </button>
        </article>
      ))}
    </div>
  );
}

function groupCampaigns(campaigns: Campaign[]) {
  const sorted = [...campaigns].sort((left, right) => {
    if (left.hasUnread !== right.hasUnread) return left.hasUnread ? -1 : 1;
    return (
      new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()
    );
  });
  return {
    active: sorted.filter((item) => item.status === "ACTIVE"),
    preparing: sorted.filter((item) => item.status === "DRAFT"),
    archive: sorted.filter(
      (item) => item.status === "PAUSED" || item.status === "FINISHED",
    ),
  };
}

function statusLabel(status: Campaign["status"]) {
  switch (status) {
    case "DRAFT":
      return "Подготовка участников и мира";
    case "ACTIVE":
      return "Кампания продолжается";
    case "PAUSED":
      return "Кампания приостановлена";
    case "FINISHED":
      return "Хроника завершена";
  }
}

export default App;
